class AdaptiveStickyCta extends HTMLElement {
  static HIDDEN_CLASS = 'tw-translate-y-full';
  static REVERSED_CLASS = 'is-reversed';
  static DEFAULTS = {
    trigger: '[data-adaptive-hero-cta]',
    hideZones: '[data-adaptive-sticky-hide]',
    footer: '.footer'
  };

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const { trigger, hideZones, footer } = AdaptiveStickyCta.DEFAULTS;

    this.trigger = document.querySelector(this.dataset.trigger || trigger);
    this.hideZones = document.querySelectorAll(
      this.dataset.hideZones || hideZones
    );
    this.footer = document.querySelector(this.dataset.footer || footer);

    this.triggerVisible = !!this.trigger;
    this.hideZoneOverlap = false;

    this.updateVisibility = this.updateVisibility.bind(this);
    this.updateScrollState = this.updateScrollState.bind(this);

    this.setupTriggerObserver();
    this.setupScrollListener();

    this.updateVisibility();
  }

  disconnectedCallback() {
    this.triggerObserver?.disconnect();
    document.removeEventListener('scroll', this.updateScrollState);
    window.removeEventListener('resize', this.updateScrollState);
  }

  setupTriggerObserver() {
    if (!this.trigger) return;

    this.triggerObserver = new IntersectionObserver(
      ([entry]) => {
        this.triggerVisible = entry.isIntersecting;
        this.updateVisibility();
      },
      { threshold: 0 }
    );

    this.triggerObserver.observe(this.trigger);
  }

  setupScrollListener() {
    if (!this.footer && !this.hideZones.length) return;

    this.updateScrollState();
    document.addEventListener('scroll', this.updateScrollState, {
      passive: true
    });
    window.addEventListener('resize', this.updateScrollState, {
      passive: true
    });
  }

  updateVisibility() {
    const shouldHide = this.triggerVisible || this.hideZoneOverlap;
    this.classList.toggle(AdaptiveStickyCta.HIDDEN_CLASS, shouldHide);
  }

  updateScrollState() {
    const ctaHeight = this.offsetHeight;
    const ctaBottom = window.innerHeight;
    const ctaTop = ctaBottom - ctaHeight;

    if (this.footer) {
      const footerRect = this.footer.getBoundingClientRect();
      const overlapping =
        ctaBottom > footerRect.top && ctaTop < footerRect.bottom;
      this.classList.toggle(AdaptiveStickyCta.REVERSED_CLASS, overlapping);
    }

    if (this.hideZones.length) {
      const isDesktop = window.innerWidth >= 1024;
      let overlap = false;
      for (const zone of this.hideZones) {
        if (
          isDesktop &&
          zone.hasAttribute('data-adaptive-sticky-hide-mobile-only')
        )
          continue;
        const zoneRect = zone.getBoundingClientRect();
        if (ctaBottom > zoneRect.top && ctaTop < zoneRect.bottom) {
          overlap = true;
          break;
        }
      }
      if (this.hideZoneOverlap !== overlap) {
        this.hideZoneOverlap = overlap;
        this.updateVisibility();
      }
    }
  }
}

if (!customElements.get('adaptive-sticky-cta')) {
  customElements.define('adaptive-sticky-cta', AdaptiveStickyCta);
}

const AdaptiveHeroCtaScroll = {
  SELECTOR: '[data-adaptive-hero-cta]',
  TARGET_ID: 'adaptive-collection-slider',

  init() {
    const triggers = document.querySelectorAll(this.SELECTOR);
    if (!triggers.length) return;

    const target = document.getElementById(this.TARGET_ID);
    if (!target) return;

    triggers.forEach(trigger => {
      if (trigger.dataset.scrollBound === 'true') return;
      trigger.dataset.scrollBound = 'true';
      trigger.addEventListener('click', event => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;

        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () =>
    AdaptiveHeroCtaScroll.init()
  );
} else {
  AdaptiveHeroCtaScroll.init();
}
